import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import { kbRulesAPI, errMsg, fmtDate, safeHref } from '../services/intelligenceApi';
import { RoleChips } from './IntelligenceKb';
import { SignoffBadge } from './IntelligenceKbRules';
import './Intelligence.css';

/* INTELLIGENCE → KB RULES → one rule: what it says, its review state and history, and the two changes a KB
 * reviewer can start here. Neither changes the KB directly: "Approve as is" and "Propose edit" each queue a KB
 * factory job; the factory runs the engine gates and the draft waits in KB REVIEW (legal tier), where it is
 * approved and published like every factory draft. Everything is rendered as text. */

const CONF = ['high', 'moderate', 'low'];
const DEADLINE_NUM = ['month', 'day', 'offset_days', 'offset_months', 'extension_month', 'extension_day', 'day_of_month'];
const IN_FLIGHT = ['queued', 'drafting'];
const clone = (x) => JSON.parse(JSON.stringify(x));
const show = (v) => (v === undefined ? '—' : v === null ? 'null' : typeof v === 'string' ? v : JSON.stringify(v));

/** A trigger as nested readable conditions: {all|any|none: [...]} groups, {field, op, value} leaves. */
function Conditions({ node }) {
  if (!node || typeof node !== 'object') return <span className="in-muted">—</span>;
  if (Array.isArray(node)) return <ul className="in-plain">{node.map((n, i) => <li key={i}><Conditions node={n} /></li>)}</ul>;
  const group = ['all', 'any', 'none', 'not'].find((k) => k in node);
  if (group) {
    const items = Array.isArray(node[group]) ? node[group] : [node[group]];
    const label = { all: 'all of', any: 'any of', none: 'none of', not: 'not' }[group];
    return (
      <div>
        <span className="in-muted">{label}:</span>
        <ul className="in-plain">{items.map((n, i) => <li key={i}><Conditions node={n} /></li>)}</ul>
      </div>
    );
  }
  if ('field' in node) {
    const rest = Object.fromEntries(Object.entries(node).filter(([k]) => !['field', 'op', 'value'].includes(k)));
    return (
      <span>
        <span className="in-mono">{node.field}</span> <b>{node.op || '?'}</b> <span className="in-mono">{show(node.value)}</span>
        {Object.keys(rest).length > 0 && <span className="in-muted"> {JSON.stringify(rest)}</span>}
      </span>
    );
  }
  return <span className="in-mono">{JSON.stringify(node)}</span>;
}

function Summary({ data }) {
  const r = data.record;
  const src = r.source || {};
  const href = safeHref(src.url);
  const sc = data.sidecar;
  return (
    <div className="in-card">
      <h3>What it says</h3>
      {r.verdict && <p style={{ margin: '0 0 6px' }}><b>{r.verdict}</b></p>}
      <p style={{ marginTop: 0 }}>{r.plain_language || <span className="in-muted">No plain-language summary.</span>}</p>
      {(r.action_steps || []).length > 0 && (
        <>
          <h4>Action steps</h4>
          <ul className="in-plain">{r.action_steps.map((s, i) => <li key={i}>{s}</li>)}</ul>
        </>
      )}
      <h4>When it applies</h4>
      {r.trigger ? <Conditions node={r.trigger} /> : <p className="in-muted">No trigger (matched by {r.entity_match ? 'entity_match' : r.txn_match ? 'txn_match' : 'its own rules'}).</p>}
      {r.deadline && (
        <>
          <h4>Deadline</h4>
          <p className="in-mono" style={{ margin: 0 }}>{JSON.stringify(r.deadline)}</p>
        </>
      )}
      {r.due_date_rule && (
        <>
          <h4>Due rule</h4>
          <p className="in-mono" style={{ margin: 0 }}>{JSON.stringify(r.due_date_rule)}</p>
        </>
      )}
      <h4>Source</h4>
      <p style={{ margin: 0 }}>
        {href ? <a href={href} target="_blank" rel="noopener noreferrer">{src.title || href}</a> : (src.title || <span className="in-muted">no source url</span>)}
        {src.section && <span className="in-muted"> · {src.section}</span>}
        {src.retrieved_at && <span className="in-muted"> · retrieved {src.retrieved_at}</span>}
      </p>
      {src.quote ? <div className="in-quote"><span className="in-muted">Quote</span>{src.quote}</div> : <p className="in-muted">No quote on the record.</p>}
      {src.document_sha256 && <p className="in-muted in-mono">snapshot {src.document_sha256.slice(0, 16)}…</p>}
      {sc && (
        <>
          <h4>Quote sidecar ({data.sidecar_state}{data.pending_upgrade ? ', upgrade not signed' : ''})</h4>
          <div className="in-quote"><span className="in-muted">{(sc.source || {}).title || (sc.source || {}).url}</span>{(sc.source || {}).quote}</div>
        </>
      )}
      <p className="in-muted" style={{ marginBottom: 0 }}>
        {r.record_kind} · {(r.jurisdiction || []).join(', ')} · {r.domain} · confidence {r.confidence || '—'}
        {r.effective_from ? ` · effective ${r.effective_from}` : ''}{r.review_by ? ` · review by ${r.review_by}` : ''} · file {data.file}
      </p>
    </div>
  );
}

function GateChips({ gates }) {
  if (!gates) return <span className="in-muted">not run yet</span>;
  return (
    <span className="in-chips">
      {Object.entries(gates).map(([g, v]) => <span key={g} className={`in-badge ${v === 'ok' ? 'ok' : 'bad'}`}>{g} {v}</span>)}
    </span>
  );
}

function ReviewState({ data }) {
  const rv = data.review;
  const open = data.open_draft;
  const latest = data.drafts[0];
  return (
    <div className="in-card">
      <h3>Review</h3>
      <div className="in-row">
        <SignoffBadge state={data.signoff} />
        {rv && rv.signed_off && <span className="in-muted">signed {rv.ref} by {rv.by} on {rv.on} · {rv.tier}</span>}
        {rv && !rv.signed_off && <span className="in-muted">has a review block, not signed: staff-only until approved</span>}
        {!rv && <span className="in-muted">no review block: shown to members (grandfathered), never signed off under E19</span>}
      </div>
      {data.queued_job && (
        <div className="in-banner info" style={{ marginTop: 10 }}>
          {data.queued_job.kind === 'manual_signoff' ? 'Approve as is' : 'Edit'} queued as job {data.queued_job.id} ({data.queued_job.status}): the KB factory runs the engine gates on its next tick.
        </div>
      )}
      {open && (
        <div className="in-banner warn" style={{ marginTop: 10 }}>
          Draft <Link to={`/intelligence/kb/drafts/${open.id}`}>{open.id}</Link> ({open.action}) is {open.status}.
          {open.status === 'in_review' ? ' Review, approve and publish it in KB REVIEW.' : open.status === 'approved' ? ' It is approved: publish it from the factory.' : ''}
        </div>
      )}
      {latest && (
        <>
          <h4>Latest draft · gate results</h4>
          <div className="in-row">
            <Link to={`/intelligence/kb/drafts/${latest.id}`} className="in-mono">draft {latest.id}</Link>
            <span className="in-badge">{latest.status}</span>
            <GateChips gates={latest.gates} />
          </div>
          {(latest.errors || []).length > 0 && <ul className="in-violations">{latest.errors.map((e, i) => <li key={i}>{e}</li>)}</ul>}
          {(latest.deltas || []).length > 0 && <p className="in-muted">Fixture deltas: {latest.deltas.join(', ')}</p>}
        </>
      )}
      {data.jobs.length > 0 && (
        <>
          <h4>Changes started here</h4>
          <div className="in-table-wrap">
            <table className="in-table">
              <thead><tr><th>Job</th><th>Kind</th><th>Status</th><th>By</th><th>Why</th><th>When</th></tr></thead>
              <tbody>
                {data.jobs.map((j) => (
                  <tr key={j.id}>
                    <td className="in-mono">{j.id}</td>
                    <td>{j.kind === 'manual_signoff' ? 'approve as is' : 'edit'}</td>
                    <td><span className={`in-badge ${j.status === 'failed' ? 'bad' : ''}`}>{j.status}</span>{j.error && <div className="in-red" style={{ fontSize: 12, maxWidth: 420 }}>{j.error}</div>}</td>
                    <td className="in-muted">{j.created_by}</td>
                    <td className="in-muted" style={{ maxWidth: 320 }}>{j.reason || '—'}</td>
                    <td className="in-muted">{fmtDate(j.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
      <h4>History</h4>
      {!data.drafts.length ? <p className="in-muted">No KB factory drafts for this rule yet.</p> : (
        <div className="in-table-wrap">
          <table className="in-table">
            <thead><tr><th>Draft</th><th>Change</th><th>Status</th><th>Tier</th><th>Gates</th><th>Updated</th></tr></thead>
            <tbody>
              {data.drafts.map((d) => (
                <tr key={d.id}>
                  <td><Link className="in-mono" to={`/intelligence/kb/drafts/${d.id}`}>{d.id}</Link></td>
                  <td>{d.action} · {d.verdict || '—'}</td>
                  <td><span className="in-badge">{d.status}</span></td>
                  <td>{d.tier}</td>
                  <td><GateChips gates={d.gates} /></td>
                  <td className="in-muted">{fmtDate(d.updated_at || d.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {data.reviews.length > 0 && (
        <>
          <h4>Decisions</h4>
          <ul className="in-plain">
            {data.reviews.map((r) => (
              <li key={r.id}>
                {fmtDate(r.created_at)} · {r.admin_name || r.admin_email || `admin ${r.admin_id}`} ({r.role}) · <b>{r.decision}</b>
                {r.signoff_id ? ` · ${r.signoff_id}` : ''} · draft {r.draft_id}{r.notes ? ` · ${r.notes}` : ''}
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

function ApproveAsIs({ data, onDone }) {
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const v = data.viewer;
  const go = async () => {
    if (!window.confirm(`Approve ${data.record.id} as it is? The KB factory runs the engine gates on it, then it waits in KB REVIEW${v.approval_applies ? ' already approved by you' : ' for a legal approver'}.`)) return;
    setBusy(true);
    setMsg(null);
    try {
      const r = await kbRulesAPI.approve(data.record.id, { note });
      onDone(r.data.message);
    } catch (e) {
      setMsg(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="in-card">
      <h3>Approve as is</h3>
      {!v.can_approve ? <p className="in-muted">{v.approve_why_not}</p> : (
        <>
          <p className="in-muted" style={{ marginTop: 0 }}>
            Signs this rule exactly as it is (adds its review block). {v.approval_applies
              ? 'Your approval is recorded once the engine gates pass; the draft is then ready to publish.'
              : 'Manual drafts are legal tier: a legal approver approves it in KB REVIEW.'}
          </p>
          {data.signoff_hints.length > 0 && (
            <div className="in-banner warn">
              A signed rule must carry {data.signoff_hints.join(', ')}. This rule lacks them, so the lint gate will refuse it as it is: propose an edit that adds them instead.
            </div>
          )}
          <label className="in-field">Note (optional)<textarea className="in-textarea" value={note} onChange={(e) => setNote(e.target.value)} maxLength={2000} /></label>
          {msg && <div className="in-banner bad">{msg}</div>}
          <button type="button" className="in-btn primary" disabled={busy} onClick={go}>Approve as is</button>
        </>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return <label className="in-field">{label}{children}</label>;
}

/** Numeric leaves under compute.params, as [path array, value]. */
function paramLeaves(obj, path = []) {
  if (typeof obj === 'number') return [[path, obj]];
  if (obj && typeof obj === 'object') return Object.entries(obj).flatMap(([k, v]) => paramLeaves(v, [...path, k]));
  return [];
}
const setIn = (obj, path, value) => {
  let o = obj;
  for (const k of path.slice(0, -1)) o = o[k];
  o[path[path.length - 1]] = value;
};

function EditForm({ rec, setRec }) {
  const upd = (fn) => setRec((r) => { const n = clone(r); fn(n); return n; });
  const num = (v) => (v === '' ? null : Number(v));
  const params = rec.compute && rec.compute.params ? paramLeaves(rec.compute.params) : [];
  return (
    <div>
      <Field label="Title"><input className="in-input" value={rec.title || ''} onChange={(e) => upd((n) => { n.title = e.target.value; })} /></Field>
      <Field label="Plain language"><textarea className="in-textarea" value={rec.plain_language || ''} onChange={(e) => upd((n) => { n.plain_language = e.target.value; })} /></Field>
      <Field label="Verdict"><input className="in-input" value={rec.verdict || ''} onChange={(e) => upd((n) => { n.verdict = e.target.value; })} /></Field>
      <Field label="Confidence">
        <select className="in-select" value={rec.confidence || ''} onChange={(e) => upd((n) => { n.confidence = e.target.value; })}>
          {!CONF.includes(rec.confidence) && <option value={rec.confidence || ''}>{rec.confidence || '—'}</option>}
          {CONF.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </Field>
      <div className="in-grid2">
        <Field label="Source URL"><input className="in-input" value={(rec.source || {}).url || ''} onChange={(e) => upd((n) => { n.source = { ...(n.source || {}), url: e.target.value }; })} /></Field>
        <Field label="Source name"><input className="in-input" value={(rec.source || {}).title || ''} onChange={(e) => upd((n) => { n.source = { ...(n.source || {}), title: e.target.value }; })} /></Field>
      </div>
      {rec.deadline && (
        <>
          <h4>Deadline</h4>
          <div className="in-grid3">
            <Field label="label"><input className="in-input" value={rec.deadline.label || ''} onChange={(e) => upd((n) => { n.deadline.label = e.target.value; })} /></Field>
            {DEADLINE_NUM.filter((k) => k in rec.deadline).map((k) => (
              <Field key={k} label={k}>
                <input className="in-input" type="number" value={rec.deadline[k] ?? ''} onChange={(e) => upd((n) => { n.deadline[k] = num(e.target.value); })} />
              </Field>
            ))}
          </div>
        </>
      )}
      {rec.due_date_rule && <p className="in-muted">This rule has a due_date_rule: edit it in the raw JSON tab.</p>}
      {params.length > 0 && (
        <>
          <h4>Amounts (compute.params)</h4>
          <div className="in-grid3">
            {params.map(([p, v]) => (
              <Field key={p.join('.')} label={p.join('.')}>
                <input className="in-input" type="number" value={v ?? ''} onChange={(e) => upd((n) => setIn(n.compute.params, p, num(e.target.value)))} />
              </Field>
            ))}
          </div>
        </>
      )}
      <p className="in-muted">Everything else (trigger, scope, action steps, quote, dates…) is in the raw JSON tab.</p>
    </div>
  );
}

function Editor({ data, onDone, onCancel }) {
  const original = useMemo(() => {
    const r = clone(data.record);
    delete r.review;
    return r;
  }, [data.record]);
  const [rec, setRec] = useState(original);
  const [tab, setTab] = useState('form');
  const [raw, setRaw] = useState('');
  const [rawErr, setRawErr] = useState(null);
  const [truths, setTruths] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  const toRaw = () => { setRaw(JSON.stringify(rec, null, 2)); setRawErr(null); setTab('raw'); };
  const parseRaw = () => {
    try {
      const v = JSON.parse(raw);
      if (!v || typeof v !== 'object' || Array.isArray(v)) throw new Error('the record must be a JSON object');
      return v;
    } catch (e) {
      setRawErr(`Not valid JSON: ${e.message}`);
      return null;
    }
  };
  const toForm = () => {
    const v = parseRaw();
    if (v) { setRec(v); setTab('form'); }
  };
  const changed = useMemo(() => {
    const cur = tab === 'raw' ? (() => { try { return JSON.parse(raw); } catch { return null; } })() : rec;
    if (!cur) return [];
    const a = original;
    return [...new Set([...Object.keys(a), ...Object.keys(cur)])].filter((k) => JSON.stringify(a[k]) !== JSON.stringify(cur[k]));
  }, [tab, raw, rec, original]);

  const submit = async () => {
    const record = tab === 'raw' ? parseRaw() : rec;
    if (!record) return;
    if (!reason.trim()) { setMsg('Say why: the reviewer reads it next to the diff.'); return; }
    let t = null;
    if (truths.trim()) {
      try {
        t = JSON.parse(truths);
      } catch (e) {
        setMsg(`Truths are not valid JSON: ${e.message}`);
        return;
      }
    }
    setBusy(true);
    setMsg(null);
    try {
      const r = await kbRulesAPI.edit(data.record.id, { record, reason, truths: t });
      onDone(r.data.message);
    } catch (e) {
      setMsg(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="in-card">
      <div className="in-row">
        <h3 style={{ margin: 0 }}>Edit {data.record.id}</h3>
        <span className="in-spacer" />
        <button type="button" className="in-btn small" onClick={onCancel}>Cancel</button>
      </div>
      <div className="in-tabs" role="tablist" style={{ marginTop: 10 }}>
        <button type="button" role="tab" aria-selected={tab === 'form'} className={`in-tab ${tab === 'form' ? 'active' : ''}`} onClick={() => tab === 'raw' && toForm()}>FORM</button>
        <button type="button" role="tab" aria-selected={tab === 'raw'} className={`in-tab ${tab === 'raw' ? 'active' : ''}`} onClick={() => tab === 'form' && toRaw()}>RAW JSON</button>
      </div>
      {tab === 'form' ? <EditForm rec={rec} setRec={setRec} /> : (
        <>
          <textarea className="in-textarea in-mono" style={{ minHeight: 420 }} value={raw} onChange={(e) => { setRaw(e.target.value); setRawErr(null); }} spellCheck={false} />
          {rawErr && <div className="in-banner bad">{rawErr}</div>}
          <details style={{ marginTop: 8 }}>
            <summary className="in-muted">Truths (optional, advanced)</summary>
            <p className="in-muted">By default the draft pins what this rule does today on the golden profiles (fires / does not fire). If your edit changes where it fires, give the new truths here as a JSON list, each naming {data.record.id} in must_fire or must_not_fire, with a name and a profile.</p>
            <textarea className="in-textarea in-mono" value={truths} onChange={(e) => setTruths(e.target.value)} placeholder='[{"name": "…", "profile": "kim", "must_fire": ["…"]}]' />
          </details>
        </>
      )}
      <p className="in-muted">{changed.length ? `Changes: ${changed.join(', ')}` : 'No changes yet.'} The review block cannot be edited: a sign-off comes only from a decision.</p>
      <Field label="Why (required)"><textarea className="in-textarea" value={reason} onChange={(e) => setReason(e.target.value)} maxLength={2000} /></Field>
      {msg && <div className="in-banner bad">{msg}</div>}
      <button type="button" className="in-btn primary" disabled={busy || !changed.length} onClick={submit}>Propose edit</button>
    </div>
  );
}

export default function IntelligenceKbRecord() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(false);
  const [flash, setFlash] = useState(null);

  const load = useCallback(async (fresh) => {
    try {
      const r = await kbRulesAPI.get(id, fresh);
      setData(r.data);
      setError(null);
    } catch (e) {
      setError(errMsg(e));
    }
  }, [id]);
  useEffect(() => { setData(null); load(); }, [load]);

  // while the factory works on a change, look again every 10 s so its gate results show when ready
  const waiting = !!data && (!!data.queued_job || (data.open_draft && ['drafted', 'revalidate'].includes(data.open_draft.status))
    || (data.jobs[0] && IN_FLIGHT.includes(data.jobs[0].status)));
  useEffect(() => {
    if (!waiting) return undefined;
    const t = setInterval(() => load(), 10_000);
    return () => clearInterval(t);
  }, [waiting, load]);

  const done = (text) => { setFlash(text); setEditing(false); load(); };
  const r = data && data.record;
  return (
    <div className="dashboard-layout">
      <Sidebar />
      <main className="dashboard-main">
        <div className="dashboard-header">
          <div className="header-left">
            <p className="in-muted" style={{ margin: 0 }}><Link to="/intelligence/kb/rules">← KB RULES</Link></p>
            <h1 className="page-title in-mono" style={{ wordBreak: 'break-all' }}>{id}</h1>
            {r && (
              <div className="in-row">
                <SignoffBadge state={data.signoff} />
                <span className={`in-badge ${r.confidence === 'high' ? 'ok' : ''}`}>{r.confidence}</span>
                {data.has_quote ? <span className="in-badge ok">quote</span> : <span className="in-badge">no quote</span>}
                {data.overrides && <span className="in-badge">records.d override of {data.overrides}</span>}
                <span className="in-muted">{r.title}</span>
              </div>
            )}
          </div>
          <RoleChips viewer={data?.viewer} />
        </div>
        {error && <div className="in-banner bad">{error}</div>}
        {flash && <div className="in-banner info">{flash}</div>}
        {!data ? (!error && <div className="in-empty">Loading the rule…</div>) : (
          <>
            {waiting && <div className="in-banner info">The KB factory is working on a change to this rule; this page refreshes until its gate results are in.</div>}
            <div className="in-grid2">
              <Summary data={data} />
              <ReviewState data={data} />
            </div>
            {editing ? <Editor data={data} onDone={done} onCancel={() => setEditing(false)} /> : (
              <div className="in-grid2">
                <ApproveAsIs data={data} onDone={done} />
                <div className="in-card">
                  <h3>Propose an edit</h3>
                  {!data.viewer.can_edit ? <p className="in-muted">{data.viewer.edit_why_not}</p> : (
                    <>
                      <p className="in-muted" style={{ marginTop: 0 }}>Edit the common fields in a form or the whole rule as JSON. The edit becomes a KB factory draft: the engine gates run on it, then it waits in KB REVIEW (legal tier) with its diff.</p>
                      <button type="button" className="in-btn primary" onClick={() => setEditing(true)}>Edit</button>
                    </>
                  )}
                </div>
              </div>
            )}
            <div className="in-card">
              <details>
                <summary className="in-muted">Full rule JSON</summary>
                <pre className="in-pre">{JSON.stringify(r, null, 2)}</pre>
              </details>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
